import React, { useState, useEffect } from "react";
import {
    Card,
    Radio,
    Typography,
    Tag,
    Space,
    Image,
    Spin,
    message,
    Grid,
    List,
    Button,
    Modal,
    InputNumber,
    Popconfirm,
    Row,
    Col,
    Select,
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    EditOutlined,
    CalendarOutlined,
    AppstoreOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const getStoredToken = () => {
    const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
    for (const k of keys) {
        const v = localStorage.getItem(k);
        if (v) return v.replace(/^"|"$/g, "");
    }
    try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user?.token || user?.accessToken || null;
    } catch {
        return null;
    }
};

const SupplierOrders = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadType, setLoadType] = useState("pending");

    // Filter states
    const [selectedSupplier, setSelectedSupplier] = useState("All");
    const [selectedUser, setSelectedUser] = useState("All");

    // State for Update Timeline Modal
    const [updateModalVisible, setUpdateModalVisible] = useState(false);
    const [selectedPO, setSelectedPO] = useState(null);
    const [updateDays, setUpdateDays] = useState(1);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchOrders(loadType);
    }, [loadType]);

    const fetchOrders = async (type) => {
        setLoading(true);
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const baseUrl = global.config?.APIURL?.BaseURL || "";

        try {
            if (type === "pending") {
                const res = await axios.get(
                    `${baseUrl}api/SupplierOrder/GetOrdersOfSupplierforList?IsCompleted=false`,
                    { headers }
                );
                const data = Array.isArray(res.data) ? res.data : [];
                const sortedData = [...data].sort((a, b) => {
                    const timeA = a.EstimatedDate ? new Date(a.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    const timeB = b.EstimatedDate ? new Date(b.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    if (timeA !== timeB) return timeA - timeB;
                    return b.ID - a.ID;
                });
                setOrders(sortedData);
            } else if (type === "all") {
                const [pendingRes, completedRes] = await Promise.all([
                    axios.get(
                        `${baseUrl}api/SupplierOrder/GetOrdersOfSupplierforList?IsCompleted=false`,
                        { headers }
                    ).catch(() => ({ data: [] })),
                    axios.get(
                        `${baseUrl}api/SupplierOrder/GetOrdersOfSupplierforList?IsCompleted=true`,
                        { headers }
                    ).catch(() => ({ data: [] })),
                ]);

                const pendingData = Array.isArray(pendingRes.data)
                    ? pendingRes.data
                    : [];
                const completedData = Array.isArray(completedRes.data)
                    ? completedRes.data
                    : [];

                const merged = [...pendingData, ...completedData];
                merged.sort((a, b) => {
                    const timeA = a.EstimatedDate ? new Date(a.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    const timeB = b.EstimatedDate ? new Date(b.EstimatedDate).getTime() : Number.MAX_SAFE_INTEGER;
                    if (timeA !== timeB) return timeA - timeB;
                    return b.ID - a.ID;
                });

                setOrders(merged);
            }
        } catch (err) {
            console.error("Error fetching supplier orders:", err);
            message.error("Failed to load supplier orders.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDays = async () => {
        if (updateDays === null || updateDays === undefined) {
            message.error("Please enter a valid number of days");
            return;
        }
        setSubmitting(true);
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const baseUrl = global.config?.APIURL?.BaseURL || "";

        try {
            await axios.post(
                `${baseUrl}api/SupplierOrder/UpdateDays?POID=${selectedPO.ID}&Days=${updateDays}`,
                {},
                { headers }
            );
            message.success("Timeline updated successfully!");
            setUpdateModalVisible(false);
            setUpdateDays(1);
            fetchOrders(loadType);
        } catch (err) {
            console.error(err);
            message.error("Failed to update timeline.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCompleteOrder = async (poid) => {
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const baseUrl = global.config?.APIURL?.BaseURL || "";

        try {
            await axios.post(
                `${baseUrl}api/PurchaseOrder/MarkPOasComplete?POID=${poid}`,
                {},
                { headers }
            );
            message.success("Order marked as complete!");
            fetchOrders(loadType);
        } catch (err) {
            console.error(err);
            message.error("Failed to complete order.");
        }
    };

    const suppliers = ["All", ...new Set(orders.map(o => o.SupplierName).filter(Boolean))];
    const users = ["All", ...new Set(orders.map(o => o.User).filter(Boolean))];

    const filteredOrders = orders.filter(o => {
        const matchSupplier = selectedSupplier === "All" || o.SupplierName === selectedSupplier;
        const matchUser = selectedUser === "All" || o.User === selectedUser;
        return matchSupplier && matchUser;
    });

    return (
        <div
            style={{
                padding: isMobile ? "12px" : "24px",
                background: "#f5f6fa",
                minHeight: "100vh",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
            >
                <Title level={isMobile ? 4 : 3} style={{ margin: 0, color: "#1890ff" }}>
                    Supplier Orders
                </Title>
                <Radio.Group
                    value={loadType}
                    onChange={(e) => setLoadType(e.target.value)}
                    buttonStyle="solid"
                    size={isMobile ? "middle" : "large"}
                >
                    <Radio.Button value="pending">Load Pending Only</Radio.Button>
                    <Radio.Button value="all">Load All</Radio.Button>
                </Radio.Group>
            </div>

            <div style={{ display: "flex", gap: "16px", marginBottom: "16px", flexWrap: "wrap" }}>
                <Select
                    value={selectedSupplier}
                    onChange={setSelectedSupplier}
                    style={{ minWidth: 200 }}
                    showSearch
                    placeholder="Filter by Supplier"
                >
                    {suppliers.map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}
                </Select>
                <Select
                    value={selectedUser}
                    onChange={setSelectedUser}
                    style={{ minWidth: 200 }}
                    showSearch
                    placeholder="Filter by User"
                >
                    {users.map(u => <Select.Option key={u} value={u}>{u}</Select.Option>)}
                </Select>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <List
                    grid={{
                        gutter: 16,
                        xs: 1,
                        sm: 1,
                        md: 2,
                        lg: 2,
                        xl: 3,
                        xxl: 4,
                    }}
                    dataSource={filteredOrders}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    renderItem={(record) => {
                        const isOverdue =
                            !record.IsCompleted &&
                            record.EstimatedDate &&
                            moment(record.EstimatedDate).startOf("day").diff(moment().startOf("day"), "days") < 0;

                        return (
                            <List.Item>
                                <Card
                                    hoverable
                                    style={{
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                                        backgroundColor: isOverdue ? "#fff1f0" : "#ffffff",
                                        border: "1px solid #f0f0f0"
                                    }}
                                    bodyStyle={{ padding: "16px" }}
                                >
                                    {/* Header Section */}
                                    <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                                        <div style={{ flexShrink: 0 }}>
                                            {record.ImageURL ? (
                                                <Image
                                                    src={record.ImageURL}
                                                    alt="Product"
                                                    width={80}
                                                    height={80}
                                                    style={{ objectFit: "cover", borderRadius: "8px" }}
                                                    fallback="https://via.placeholder.com/80?text=No+Image"
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        background: "#f0f0f0",
                                                        borderRadius: "8px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "12px",
                                                        color: "#999",
                                                    }}
                                                >
                                                    N/A
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                            <div style={{ fontSize: "16px", fontWeight: "600", color: "#333", marginBottom: "8px", lineHeight: "1.2" }}>
                                                {record.Product ? record.Product.toUpperCase() : "N/A"}
                                            </div>
                                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                                {record.SupplierName && (
                                                    <Tag style={{ borderRadius: "4px", background: "#f5f5f5", color: "#666", border: "none", padding: "2px 8px", margin: 0 }}>
                                                        Supplier: {record.SupplierName}
                                                    </Tag>
                                                )}
                                                {record.User && (
                                                    <Tag style={{ borderRadius: "4px", background: "#f5f5f5", color: "#666", border: "none", padding: "2px 8px", margin: 0 }}>
                                                        User: {record.User}
                                                    </Tag>
                                                )}
                                                {!record.SupplierName && !record.User && (
                                                    <Tag style={{ borderRadius: "4px", background: "#f5f5f5", color: "#666", border: "none", padding: "2px 8px", margin: 0 }}>
                                                        # {record.ID}
                                                    </Tag>
                                                )}
                                            </div>
                                        </div>
                                    </div>



                                    {/* Qty and Approx Days Tags */}
                                    <div style={{ display: "flex", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
                                        <div style={{ background: "#e6fffa", border: "1px solid #87e8de", borderRadius: "6px", padding: "6px 12px", display: "flex", alignItems: "center", gap: "6px" }}>
                                            <AppstoreOutlined style={{ color: "#13c2c2" }} />
                                            <span style={{ color: "#13c2c2" }}>Qty: <strong style={{ color: "#006d75", fontSize: "14px" }}>{record.Quantity || 0} {record.Unit}</strong></span>
                                        </div>
                                        <div style={{ background: "#fff2e8", border: "1px solid #ffbb96", borderRadius: "6px", padding: "6px 12px" }}>
                                            <span style={{ color: "#fa541c" }}>Approx: <strong style={{ color: "#d4380d", fontSize: "14px" }}>{record.AproxDays || 0} days</strong></span>
                                        </div>
                                    </div>

                                    {/* Specifications Section */}
                                    <div style={{ background: "#fbfbfb", border: "1px solid #f0f0f0", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px" }}>
                                        <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Specifications</div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                            {record.Color && (
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <span style={{ color: "#333", fontSize: "14px" }}>{record.Color.split(',').length} clr</span>
                                                    <div style={{ width: "1px", height: "14px", background: "#d9d9d9" }}></div>
                                                    <span style={{ color: "#666", fontSize: "14px" }}>{record.Color}</span>
                                                </div>
                                            )}

                                            <div style={{ fontSize: "14px", color: "#666" }}>
                                                <span style={{ color: "#333" }}>Detail:</span> {record.Detail}
                                            </div>

                                            {!record.Color && !record.Detail && (
                                                <span style={{ color: "#666", fontSize: "14px" }}>No specifications</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div style={{ height: "1px", background: "#f0f0f0", marginBottom: "16px" }} />

                                    {/* Dates Section */}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                        <div>
                                            <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase" }}>Order Date</div>
                                            <div style={{ fontWeight: "500", color: "#333", marginTop: "4px", display: "flex", alignItems: "center", gap: "6px", fontSize: "14px" }}>
                                                <CalendarOutlined style={{ color: "#888" }} />
                                                {record.CreatedOn ? moment(record.CreatedOn).format("DD MMM YYYY") : (record.EstimatedDate && record.AproxDays ? moment(record.EstimatedDate).subtract(record.AproxDays, 'days').format("DD MMM YYYY") : "-")}
                                            </div>
                                        </div>

                                        {/* Days Left Pill */}
                                        {record.EstimatedDate && (() => {
                                            const diff = moment(record.EstimatedDate).startOf("day").diff(moment().startOf("day"), "days");
                                            return (
                                                <div style={{
                                                    background: diff < 0 ? "#fff1f0" : "#e6f7ff",
                                                    color: diff < 0 ? "#ff4d4f" : "#1890ff",
                                                    padding: "4px 12px",
                                                    borderRadius: "16px",
                                                    fontSize: "12px",
                                                    border: `1px solid ${diff < 0 ? "#ffa39e" : "#91d5ff"}`
                                                }}>
                                                    {diff < 0 ? `${Math.abs(diff)} days overdue` : `${diff} days left`}
                                                </div>
                                            );
                                        })()}

                                        <div style={{ textAlign: "right" }}>
                                            <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase" }}>Est. Delivery</div>
                                            <div style={{ fontWeight: "500", color: "#fa8c16", marginTop: "4px", display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", justifyContent: "flex-end" }}>
                                                <CalendarOutlined />
                                                {record.EstimatedDate ? moment(record.EstimatedDate).format("DD MMM YYYY") : "-"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div style={{ display: "flex", gap: "12px" }}>
                                        <Button
                                            icon={<EditOutlined />}
                                            style={{ flex: 1, borderRadius: "6px", height: "40px" }}
                                            onClick={() => {
                                                setSelectedPO(record);
                                                let currentRemaining = 0;
                                                if (record.EstimatedDate) {
                                                    currentRemaining = moment(record.EstimatedDate)
                                                        .startOf("day")
                                                        .diff(moment().startOf("day"), "days");
                                                }
                                                setUpdateDays(currentRemaining);
                                                setUpdateModalVisible(true);
                                            }}
                                            disabled={record.IsCompleted}
                                        >
                                            Modify Days
                                        </Button>
                                        <Popconfirm
                                            title="Mark as Complete"
                                            description="Are you sure you want to mark this order as complete?"
                                            onConfirm={() => handleCompleteOrder(record.ID)}
                                            okText="Yes"
                                            cancelText="No"
                                            disabled={record.IsCompleted}
                                        >
                                            <Button
                                                type="primary"
                                                icon={<CheckCircleOutlined />}
                                                style={{ flex: 1, borderRadius: "6px", height: "40px", background: record.IsCompleted ? undefined : "#52c41a", borderColor: record.IsCompleted ? undefined : "#52c41a" }}
                                                disabled={record.IsCompleted}
                                            >
                                                Complete
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                </Card>
                            </List.Item>
                        );
                    }}
                />
            )}

            {/* Modal for Updating Timeline */}
            <Modal
                title={`Update Timeline - PO #${selectedPO?.ID}`}
                open={updateModalVisible}
                onOk={handleUpdateDays}
                onCancel={() => {
                    setUpdateModalVisible(false);
                    setUpdateDays(1);
                }}
                confirmLoading={submitting}
                okText="Update"
            >
                <div style={{ padding: "20px 0" }}>
                    <Space direction="vertical" style={{ width: "100%" }} size="large">
                        <Text>Please enter the number of days to update the timeline:</Text>
                        <div>
                            <Text strong style={{ marginRight: 8 }}>
                                Days:
                            </Text>
                            <InputNumber
                                value={updateDays}
                                onChange={setUpdateDays}
                                style={{ width: "100px" }}
                            />
                        </div>
                    </Space>
                </div>
            </Modal>
        </div>
    );
};

export default SupplierOrders;
